import { ListModel } from 'shared/interfaces/core-interfaces';

export interface PayrollSliceModel {
    statusCode: string;
    message: string;
    showNotification: boolean;
    isLoading: boolean;
}

// ─── Employee dropdown ─────────────────────────────────────────────────────────

// Shape returned by GET api/employee/getAllActiveByTenant (the fields we use for the
// employee dropdown). The endpoint resolves the tenant from the auth token and returns
// only that tenant's active employees, ordered by last name.
export interface PayrollEmployeeListItemBackend {
    id: string | null;
    firstName: string;
    lastName: string;
    abbreviation: string;
}

// ─── Settings ────────────────────────────────────────────────────────────────

export interface PayrollSettingItem {
    payrollColumnTypeId: number;
    columnName: string;
    columnDescription: string;
    isUsed: boolean;
}

export interface GetPayrollSettingsResponse {
    settings: PayrollSettingItem[];
    // When true (default), an employee's end-of-month minus (negative Difference) is shown as owed
    // in payroll and carried over. When false, the minus is not shown (Difference floored at 0).
    // Payroll-view only — the overtime ledger always carries the minus regardless.
    carryMinusHoursToNextMonth: boolean;
}

export interface SavePayrollSettingsPayload {
    settings: PayrollSettingItem[];
    carryMinusHoursToNextMonth: boolean;
}

// Column type IDs of the four settings that toggle a standalone table (rather than a wide-table
// column). Keyed by ID (not name) per backend guidance — column names may still be tweaked.
export const TABLE_VISIBILITY_SETTING_IDS = {
    absences: 34,
    refunds: 18,
    bonusesAndCommissions: 19,
    salaryDeductions: 35,
} as const;

export const TABLE_VISIBILITY_SETTING_ID_SET: ReadonlySet<number> = new Set(
    Object.values(TABLE_VISIBILITY_SETTING_IDS),
);

// Core payroll columns (the JUS spec's headline columns). They are always shown when enabled in
// Settings and are exempt from the "hide columns with no value" rule, so they don't disappear when
// a month has no data yet. Keyed by ID per backend guidance — column names may still be tweaked.
export const CORE_PAYROLL_COLUMN_IDS: ReadonlySet<number> = new Set([
    38, // Contracted hours
    1,  // Target hours
    36, // Actual hours (moved from 40 → 36 backend-side to avoid dynamic-id collision)
    39, // Difference
    20, // Total for month
]);

// ─── Backend payroll response types ──────────────────────────────────────────

export interface TranslationItem {
    language: string;
    translation: string;
}

export interface CumulativeAbsenceHoursBackend {
    absenceTypeId: string | null;
    defaultLanguage: string;
    names: TranslationItem[];
    approvedTotalIndustrial: number;
    pendingTotalIndustrial: number;
}

export interface OvertimeSummeryBackend {
    regularOverTimeIndustrial: number;
    additionalOverTimeIndustrial: number;
    workOnHolidayIndustrial: number;
    workAtNightIndustrial: number;
    workOnDaysOffIndustrial: number;
    workOnLeaveIndustrial: number;
    totalOvertimeIndustrial: number;
}

export interface MonthToDateSummeryBackend {
    targetIndustrial: number;
    currentIndustrial: number;
    differenceIndustrial: number;
    // Actual checked-in work hours (work + paid break + travel), uncapped. Used to derive "Actual hours".
    workedIndustrial: number;
}

export interface PayrollColumnValueBackend {
    payrollColumnTypeId: number;
    columnName: string;
    // Display value for the column's table cell. Backend sends every column as a STRING now
    // (breaking change, backend commit e4a0dba6d): hour-based columns carry a plain number
    // (e.g. "8.5"); the Inland/Outland/Driver allowance columns carry the sum together with its
    // currency (e.g. "150.00 EUR"). Parse with parseFloat when a numeric value is needed
    // (sorting / hide-empty) — parseFloat stops before the currency suffix.
    value: string;
}

export interface EmployeePayrollBackend {
    employeeId: string;
    employeeName: string;
    isMonthClosed: boolean;
    // NOTE: backend kept the old JSON property names but their meaning changed in the payroll rework:
    //   regularWork = "Target hours"     (contracted hours minus holidays and all absences)
    //   targetHours = "Contracted hours" (raw planned working-day hours)
    //   actualHours = "Actual hours"     (checked-in work + paid break, capped at target hours)
    // The wide table is driven by columnValues, not these fields.
    regularWork: number;
    targetHours: number;
    actualHours: number;
    // Minus (shortfall) carried in from the most recent CLOSED month. Only non-zero when the tenant's
    // "Carry minus hours to next month" setting is on; the backend returns 0 when it is off.
    carriedMinusFromLastMonth: number;
    holidayHours: number;
    columnValues: PayrollColumnValueBackend[];
    monthToDateSummery: MonthToDateSummeryBackend | null;
    overtimeSummery: OvertimeSummeryBackend | null;
    absenceHoursPerType: CumulativeAbsenceHoursBackend[];
}

// Attachment metadata surfaced by the GetPayrolls response. The backend deliberately sends
// metadata only (no base64 content, and no parent refund/absence id), so these are display-only —
// they cannot be downloaded from the payroll view.
export interface PayrollRefundFileBackend {
    id: string;
    fileName: string;
    fileType: number;
    description: string | null;
    createdBy: string;
    createdAt: string;
    fileBase64: string | null;
}

export interface PayrollAbsenceFileBackend {
    id: string;
    fileName: string;
    createdBy: string;
    createdAt: string;
    description: string | null;
}

export interface PayrollRefundBackend {
    refundId: string;
    employeeId: string;
    employeeName: string;
    date: string;
    currency: string;
    amount: number;
    tax: number;
    billNumber: string;
    description: string;
    files: PayrollRefundFileBackend[];
}

export interface PayrollBonusCommissionBackend {
    employeeId: string;
    employeeName: string;
    type: string;
    from: string;
    to: string;
    amount: number;
    currency: string;
    paymentInterval: string;
    comment: string | null;
}

export interface PayrollSalaryDeductionBackend {
    employeeId: string;
    employeeName: string;
    type: string;
    from: string;
    to: string;
    amount: number;
    currency: string;
    paymentInterval: string;
    comment: string | null;
}

export interface PayrollAbsenceBackend {
    employeeId: string;
    employeeName: string;
    from: string;
    to: string;
    absenceType: string;
    status: string;
    duration: number;
    files: PayrollAbsenceFileBackend[];
}

export interface GetPayrollsBackendResponse {
    month: number;
    year: number;
    settings: PayrollSettingItem[];
    employeePayrolls: EmployeePayrollBackend[];
    refunds: PayrollRefundBackend[];
    bonusesAndCommissions: PayrollBonusCommissionBackend[];
    salaryDeductions: PayrollSalaryDeductionBackend[];
    absences: PayrollAbsenceBackend[];
}

// ─── UI row models ────────────────────────────────────────────────────────────

export interface PayrollWideColumnDef {
    payrollColumnTypeId: number;
    columnName: string;
}

export function wideColumnAccessorKey(payrollColumnTypeId: number): string {
    return `col_${payrollColumnTypeId}`;
}

// Synthetic wide-table column for the per-employee `carriedMinusFromLastMonth` top-level field
// (not part of the backend `columnValues`). Rendered only when the "Carry minus hours to next
// month" setting is on. i18n key; en falls back to the key itself.
export const CARRIED_MINUS_ACCESSOR_KEY = 'carriedMinusFromLastMonth';
export const CARRIED_MINUS_COLUMN_LABEL = 'Carried minus from last month';

export interface AbsencesOvertimeRow {
    employeeId: string;
    employeeName: string;
    [columnKey: string]: string | number;
}

// Unified UI shape for an attachment shown in the payroll Refunds / Absences tables. Display-only:
// the payroll response carries metadata (no file content), so there is no download action.
export interface PayrollFileMeta {
    id: string;
    fileName: string;
    createdBy: string;
    createdAt: string;
    description: string | null;
}

export interface AbsenceRow {
    key: string;
    employeeName: string;
    from: string;
    to: string;
    absenceType: string;
    status: string;
    duration: number;
    files: PayrollFileMeta[];
}

export interface RefundRow {
    refundId: string;
    employeeId: string;
    employeeName: string;
    date: string;
    currency: string;
    amount: number;
    tax: number;
    billNumber: string;
    description: string;
    files: PayrollFileMeta[];
}

export interface BonusCommissionRow {
    employeeId: string;
    employeeName: string;
    type: string;
    from: string;
    to: string;
    amount: number;
    currency: string;
    paymentInterval: string;
    comment: string;
}

export interface SalaryDeductionRow {
    employeeId: string;
    employeeName: string;
    type: string;
    from: string;
    to: string;
    amount: number;
    currency: string;
    paymentInterval: string;
    comment: string;
}

export interface PayrollMonthlyDataResponse {
    settings: PayrollSettingItem[];
    absencesOvertimeColumns: PayrollWideColumnDef[];
    absencesAndOvertimeHours: AbsencesOvertimeRow[];
    absences: AbsenceRow[];
    refunds: RefundRow[];
    bonusesAndCommissions: BonusCommissionRow[];
    salaryDeductions: SalaryDeductionRow[];
}

// ─── Overtime payment types ───────────────────────────────────────────────────

export interface OvertimePaymentItemBackend {
    employeeId: string;
    employeeName: string;
    regularOvertime: number;
    additionalOvertime: number;
    workAtNight: number;
    workOnDaysOff: number;
    workOnHoliday: number;
    workOnLeave: number;
    monthPaymentRegularOvertime: number;
    monthPaymentAdditionalOvertime: number;
    monthPaymentWorkAtNight: number;
    monthPaymentWorkOnDaysOff: number;
    monthPaymentWorkOnHoliday: number;
    monthPaymentWorkOnLeave: number;
    hasAdditionalHours: boolean;
    allowWorkDuringLeave: boolean;
    savedOn: string | null;
    description: string | null;
    month: number;
    year: number;
}

export interface GetOvertimePaymentCreateDataResponse {
    payments: OvertimePaymentItemBackend[];
}

export interface OvertimePaymentRow {
    id: string;
    employeeId: string;
    employeeName: string;
    additionalHoursAvailable: number;
    additionalHoursToPay: number;
    regularOvertimeAvailable: number;
    regularOvertimeToPay: number;
    overtimeOverNightAvailable: number;
    overtimeOverNightToPay: number;
    overtimeAtWeekendsAvailable: number;
    overtimeAtWeekendsToPay: number;
    overtimeOnHolidaysAvailable: number;
    overtimeOnHolidaysToPay: number;
    overtimeOnLeavesAvailable: number;
    overtimeOnLeavesToPay: number;
    savedOn: string | null;
    description: string;
}

export interface SaveOvertimePaymentRequest {
    rows: OvertimePaymentRow[];
    year: number;
    month: number;
}

// ─── Tab 3 — Daily overtime details ───────────────────────────────────────────

export interface OvertimeHoursDetailBackend {
    date: string;
    target: number;
    current: number;
    regularOverTimeHours: number;
    additionalOverTimeHours: number;
    workAtNight: number;
    workOnWeekends: number;
    workOnHoliday: number;
    workOnLeave: number;
    unfinishedHours: number;
}

export interface OvertimeHoursDetailsRow {
    date: string;
    targetH: number;
    currentH: number;
    additionalHours: number;
    regularOvertimeHours: number;
    overtimeHoursOverNight: number;
    overtimeHoursAtWeekends: number;
    overtimeHoursOnHolidays: number;
    overtimeHoursOnLeaves: number;
    minusHours: number;
    availableOvertimeHours: number;
}

// ─── Overtime hours summary (multi-employee) ──────────────────────────────────

export interface EmployeeOvertimeSummaryBackend {
    employeeId: string;
    target: number;
    current: number;
    regularOverTimeHours: number;
    additionalOverTimeHours: number;
    workAtNight: number;
    workOnWeekends: number;
    workOnHoliday: number;
    workOnLeave: number;
    unfinishedHours: number;
}

export interface OvertimeHoursSummaryRow {
    employeeId: string;
    employeeName: string;
    targetH: number;
    currentH: number;
    additionalHours: number;
    regularOvertimeHours: number;
    overtimeHoursOverNight: number;
    overtimeHoursAtWeekends: number;
    overtimeHoursOnHolidays: number;
    overtimeHoursOnLeaves: number;
    minusHours: number;
    availableOvertimeHours: number;
}

// ─── Tab 4 — Overtime payment history (per employee, all months) ───────────────

export interface OvertimePaymentHistoryItemBackend {
    id: string;
    employeeId: string;
    month: number;
    year: number;
    regularOvertime: number;
    additionalOvertime: number;
    workAtNight: number;
    workOnDaysOff: number;
    workOnHoliday: number;
    workOnLeave: number;
    totalOvertime: number;
    savedOn: string | null;
    description: string | null;
}

export interface GetOvertimePaymentHistoryResponse {
    payments: OvertimePaymentHistoryItemBackend[];
}

export interface OvertimePaymentHistoryRow {
    id: string;
    month: number;
    year: number;
    additionalHours: number;
    regularOvertimeHours: number;
    overtimeHoursOverNight: number;
    overtimeHoursAtWeekends: number;
    overtimeHoursOnHolidays: number;
    overtimeHoursOnLeaves: number;
    totalOvertimeHours: number;
    savedOn: string | null;
    description: string;
}
